import {
    ChangeDetectionStrategy,
    Component,
    computed,
    ElementRef,
    inject,
    input,
    signal,
    viewChild,
    ViewEncapsulation,
} from '@angular/core';
import { DatePipe } from '@angular/common';
import { ButtonComponent, IconComponent, LinkComponent, RootDirective, TooltipDirective } from '@pure/hive';
import { faArrowDown, faArrowUp } from '@fortawesome/pro-light-svg-icons';
import { Subject } from 'rxjs';
import {
    EoneCapacityExpansionRenderInfo,
    RECOMMENDATION_ACTION_EVENT_TOKEN,
    RecommendationActionEvent,
} from '../../../../data/models/recommendations.models';
import { registerAsDialogPlugin } from '../../services/recommendation-component-registry.service';
import { mountHighchartsChart } from '../../utils/recommendations-chart-utils';

/** Stable registry key for this plugin. Used as the lookup name on the corresponding RenderInfo. */
export const EONE_CAPACITY_EXPANSION_DIALOG_PLUGIN_NAME = 'EoneCapacityExpansionDialogPluginComponent';

/**
 * Dialog component for Evergreen//One capacity expansion recommendations.
 *
 * Displayed when the user clicks a capacity-expansion recommendation card.
 * Renders the service tier sub-header, a current-vs-recommended comparison
 * table, and a toggleable Highcharts capacity timeseries chart. Chart
 * mounting / lifecycle is delegated to `mountHighchartsChart`;
 * `RecommendationProcessingService` bakes all data, series-split, and
 * styling into `renderInfo.chartOptions` up front.
 */
@Component({
    selector: 'eone-capacity-expansion-dialog-plugin',
    templateUrl: './eone-capacity-expansion-dialog-plugin.component.html',
    styleUrls: ['./eone-capacity-expansion-dialog-plugin.component.scss'],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ButtonComponent, DatePipe, IconComponent, LinkComponent, RootDirective, TooltipDirective],
    encapsulation: ViewEncapsulation.None,
})
export class EoneCapacityExpansionDialogPluginComponent {
    renderInfo = input.required<EoneCapacityExpansionRenderInfo>();

    readonly showChart = signal(false);
    readonly faArrowUp = faArrowUp;
    readonly faArrowDown = faArrowDown;
    readonly RecommendationActionEvent = RecommendationActionEvent;

    readonly chartContainer = viewChild<ElementRef<HTMLDivElement>>('chartContainer');

    private readonly recommendationActionEvent$ = inject<Subject<RecommendationActionEvent>>(
        RECOMMENDATION_ACTION_EVENT_TOKEN,
        { optional: true },
    );

    constructor() {
        mountHighchartsChart(
            this.chartContainer,
            computed(() => this.renderInfo().chartOptions),
        );
    }

    toggleChart(event: MouseEvent): void {
        this.showChart.update(v => !v);
        (event.target as HTMLElement).blur();
    }

    handleActionEvent(action: RecommendationActionEvent): void {
        this.recommendationActionEvent$?.next(action);
    }
}

registerAsDialogPlugin(EONE_CAPACITY_EXPANSION_DIALOG_PLUGIN_NAME, EoneCapacityExpansionDialogPluginComponent);
